import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useStore } from "react-redux";
import { getToken } from "../../common/environment_config.es6";
import { updatePluginToolbar } from "../../common/actions";
import { getHostShellOrigin } from "../../common/parentOrigin.es6";
import { spinForSceneAdd } from "../utils/sceneAddSpinner";
import { createBox } from "../../common/common_tools";
import { randomPositionGenerator } from "../components/clipboard/clipboard.utils";
import { ID_PREFIX_BOX } from "../../common/constants";
import { FPS } from "../../common/sceneTimeline.es6";
import { isSlide, isContainedView } from "../../common/utils";
import IdealStudio from "../../core/editor/main";
import getMediaBoxSize from "../utils/getMediaBoxSize";
import { applyAnimationResult } from "../utils/applyAnimationResult.es6";
import { pickAnimationApplyUrl } from "../utils/pickAnimationApplyUrl.es6";
import { onJobsSettled, clearSettledImageEditJobs } from "../utils/jobSettled";
import {
  isTalkingAvatarJob,
  applyTalkingAvatarFromJob,
} from "../components/add_sound_modal/talking_avatar/talkingAvatarInsert";
import { settleTalkingAvatarJobs } from "../components/add_sound_modal/talking_avatar/talkingAvatarJobStore";
import { settleAreaOpsStorage } from "../components/merlin/area_ops/useAreaOpsQueue";
import { applyMotionResult } from "../utils/applyMotionPlan";
import AsyncWidgetApp from "../components/async-widget/App";
import "../components/async-widget/widget.css";

// Pull the generated image URL out of an approved image-generation job
// (text-to-image / style / direct). The widget's JOB_APPROVED payload shape is
// not guaranteed, so we try the known canonical paths first, then fall back to a
// deep scan for the first image-extension URL anywhere in the object (and, last
// resort, the first http URL). Returns null when nothing usable is found.
function extractGeneratedImageUrl(job) {
  if (!job || typeof job !== "object") return null;
  const isHttp = (u) => typeof u === "string" && /^https?:\/\//i.test(u.trim());
  const isImg = (u) => isHttp(u) && /\.(png|jpe?g|webp|gif|bmp|svg|avif)(\?|#|$)/i.test(u.trim());
  const firstUrl = (arr) =>
    Array.isArray(arr) ? arr.map((x) => (typeof x === "string" ? x : x?.url)).find(isHttp) : null;

  const r = job.result && typeof job.result === "object" ? job.result : {};
  const md = job.metadata && typeof job.metadata === "object" ? job.metadata : {};
  const canonical = [
    firstUrl(r.images),
    r.url,
    r.imageUrl,
    firstUrl(r.details?.generatedImages),
    firstUrl(md.generatedImages),
    firstUrl(md.images),
    md.imageUrl,
    job.resultUrl,
    job.imageUrl,
    job.url,
  ];
  for (const c of canonical) if (isHttp(c)) return c.trim();

  // Deep fallback: walk the whole object graph once. Prefer an image-extension
  // URL; remember the first plain http URL as a last resort.
  let anyHttp = null;
  const seen = new Set();
  const stack = [job];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== "object" || seen.has(cur)) continue;
    seen.add(cur);
    for (const v of Object.values(cur)) {
      if (typeof v === "string") {
        if (isImg(v)) return v.trim();
        if (!anyHttp && isHttp(v)) anyHttp = v.trim();
      } else if (v && typeof v === "object") {
        stack.push(v);
      }
    }
  }
  return anyHttp;
}

export default function JobWidgetHost({
  onBoxAdded,
  containedViews,
  containedViewSelected,
  navItems,
  navItemsIds,
  navItemSelected,
  boxSelected,
  pluginToolbars,
  boxes,
  onNavItemSelected,
  onToolbarUpdated,
  onBoxDeleted,
  onBoxLayerChange,
  onInfographicApprove,
  onInfographicSceneApprove,
  onQuestionBatchApprove,
  // Görsel Ekle'nin 3B obje / Standart hareketli görsel kutusu (EditorApp).
  onSceneVisualApprove,
  // openSignal: her artışta widget'ı dışarıdan aç.
  openSignal = 0,
  // hideFab: ChatPanel kendi launcher menüsünden açtığı için widget'ın kendi
  // FAB'ı gizlenir — tek giriş noktası openSignal olur.
  hideFab = false,
}) {
  const stagedNavIdRef = useRef(null); // approve anındaki navId
  const addFileAsBoxRef = useRef(null); // her zaman güncel addFileAsBox
  const dispatch = useDispatch();
  const store = useStore();

  const urlParams = new URLSearchParams(window.location.search);
  const trainingId = urlParams.get("id");

  /**
   * Biten avatar klibini sahnedeki AvatarPlayer kutusuna yaz.
   *
   * onToolbarUpdated DEĞİL updatePluginToolbar: ilki tek bir name/value alıyor
   * ve yalnızca AÇIK sahnenin toolbar'ına bakıyor; burada iki alan birden ve
   * çoğu zaman BAŞKA bir sahnedeki kutu yazılıyor.
   *
   * jobStatus olmadan videoUrl işe yaramaz — AvatarPlayer postere kilitli kalır
   * ve visor kutuyu video kutusu saymaz (hazırlık kapısı, sahne sonu tespiti,
   * export manifesti hep buna bakıyor).
   *
   * Aynı klibi ikinci kez yazmak zararsız, o yüzden hem otomatik tamamlanma
   * hem de Approve yolu buraya bağlanıyor.
   */
  const applyAvatarClip = useCallback(
    ({ navId, boxId, videoUrl }) => {
      if (!navId || !boxId || !videoUrl) return false;
      dispatch(
        updatePluginToolbar(
          boxId,
          "main",
          "state",
          ["videoUrl", "jobStatus"],
          [videoUrl, "done"],
          undefined,
          navId
        )
      );
      return true;
    },
    [dispatch]
  );

  /**
   * `session_job_completed` → onay beklemeden sahneye uygula.
   *
   * Yalnızca avatar-animate: AvatarPlayer kutusu üretimden beri sahnede duruyor
   * ve iş bittiğinde sadece hareketsiz posterin yerine klip geçiyor. Yazar iki
   * aday arasından seçim yapmıyor, bir yerin dolmasını bekliyor — Approve
   * tıklaması gereksiz. Kutu EKLEYEN job tipleri onay akışında kalır; ilk
   * satırdaki erken çıkış onlara hiç dokunmaz.
   */
  const handleJobCompleted = useCallback(
    (completion) => {
      const c = completion?.data ?? completion ?? {};
      if ((c.jobType || c.type) !== "avatar-animate") return;
      applyAvatarClip({
        navId: c.navId || c.metadata?.navId,
        boxId: c.boxId || c.metadata?.boxId,
        videoUrl: c.videoUrl || c.resultUrl || c.result?.videoUrl,
      });
    },
    [applyAvatarClip]
  );

  const addFileAsBox = async (fileType, url, targetNavId = null, opts = {}) => {
    if (!onBoxAdded || !url) {
      console.warn("[JobWidgetHost] addFileAsBox skipped: no onBoxAdded or url", {
        hasOnBoxAdded: !!onBoxAdded,
        url,
      });
      return;
    }

    // Bazı özel servisler (ör. Tavus) dosya tipini "file" döndürebiliyor.
    // Böyle bir durumda URL uzantısına göre tekrar sınıflandır.
    if (fileType === "file" && typeof url === "string") {
      const cleanUrl = url.split("?")[0];
      const dotIndex = cleanUrl.lastIndexOf(".");
      if (dotIndex !== -1) {
        const extFromUrl = cleanUrl.substring(dotIndex + 1).toLowerCase();
        if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(extFromUrl)) {
          fileType = "image";
        } else if (["mp3", "wav", "ogg"].includes(extFromUrl)) {
          fileType = "audio";
        } else if (["mp4", "mov", "webm", "mkv"].includes(extFromUrl)) {
          fileType = "video";
        } else if (extFromUrl === "csv") {
          fileType = "csv";
        } else if (extFromUrl === "pdf") {
          fileType = "pdf";
        }
      }
    }

    const t = (fileType || "").toLowerCase();
    let pluginName = null;

    if (t === "image") pluginName = "HotspotImages";
    else if (t === "audio") pluginName = "EnrichedAudio";
    else if (t === "video") pluginName = "EnrichedPlayer";

    if (!pluginName) {
      console.warn("[JobWidgetHost] addFileAsBox skipped: unsupported fileType", { fileType, t });
      return;
    }

    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) {
      console.warn("[JobWidgetHost] addFileAsBox skipped: plugin not registered", { pluginName });
      return;
    }

    // Use targetNavId if provided, otherwise fall back to navItemSelected
    const resolvedNavId =
      targetNavId ||
      (containedViewSelected && containedViews[containedViewSelected]
        ? containedViewSelected
        : navItemSelected);

    const navItem = navItems[resolvedNavId];

    if (!navItem) {
      console.warn("[JobWidgetHost] addFileAsBox skipped: navItem lookup failed", {
        resolvedNavId,
        targetNavId,
        navItemKeys: Object.keys(navItems || {}),
      });
      return;
    }

    const cv =
      !targetNavId &&
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);

    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    // Pozisyon + id await gerektirmez → hemen üret ve spinner'ı TIKLAMA ANINDA
    // göster (medya indirilip boyut hesaplanmadan önce). Kullanıcı bekleme hissini
    // anında alır; spinner kutu render olunca üstüne geçip medya yüklenince kapanır.
    // fullScene: görsel sahnenin tamamını kaplar (tam sığdır) → sol-üst köşe.
    const position = opts.fullScene
      ? { x: 0, y: 0, type: "absolute" }
      : {
          x: randomPositionGenerator(10, 30),
          y: randomPositionGenerator(10, 30),
          type: "absolute",
        };
    const boxId = ID_PREFIX_BOX + Date.now();
    spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv });

    // Audio still clones a selected box; image/video use the metadata helper.
    let width;
    let height;
    let widthUnit;
    let heightUnit;

    if (opts.fullScene) {
      // Sahneyi tam kapla: 100% × 100% (canvas boyutuna göre).
      width = 100;
      height = 100;
      widthUnit = "%";
      heightUnit = "%";
    } else if (t === "image" || t === "video") {
      ({ width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, t));
    } else {
      // Skip cloning when the selected box is the same plugin
      const selectedSamePlugin =
        boxSelected && pluginToolbars?.[boxSelected]?.pluginId === pluginName;

      if (!selectedSamePlugin && boxSelected && pluginToolbars && pluginToolbars[boxSelected]) {
        const selStruct = pluginToolbars[boxSelected].structure;
        if (selStruct) {
          const scale = 0.6; // shrink to 60% of original size
          const w = parseFloat(selStruct.width);
          const h = parseFloat(selStruct.height);

          width = isNaN(w) ? selStruct.width : w * scale;
          height = isNaN(h) ? selStruct.height : h * scale;
          widthUnit = selStruct.widthUnit;
          heightUnit = selStruct.heightUnit;
        }
      }
    }

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      position,
      id: boxId,
      page: cv ? containedViewSelected : navItem.id || resolvedNavId || 0,
      // TemplatesModal pattern: pass media URL directly on ids so plugin picks it as toolbar.state.url
      url,
      // If there is a selected box, clone its size units so aspect ratio & size match modal-added elements
      width,
      height,
      widthUnit,
      heightUnit,
      // Hareketli Görsel GIF/WebP: kaynak notu (pill'deki "AI ile düzenle")
      // ve 0'dan kendi süresince timeline — modaldaki ekleme ile aynı.
      ...(opts.motionSource ? { motionSource: opts.motionSource } : {}),
      ...(opts.timeline ? { timeline: opts.timeline } : {}),
      ...(opts.initialState ? { initialState: opts.initialState } : {}),
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  // addFileAsBox her render'da yeniden tanımlandığı için ref'e al. Spinner'ı
  // sceneAddSpinner util'i yönetir (kutu render olunca üstüne geçip medya
  // yüklenince kapanır) — burada ayrıca beklemeye gerek yok.
  addFileAsBoxRef.current = addFileAsBox;

  // handleJobApproved/handleAddToScene'in içindeki setTimeout callback'leri
  // gecikmeden SONRA çalışır — o ana kadar birden çok render geçmiş olabilir,
  // bu yüzden closure'dan doğrudan okumak eski (stale) prop'ları görür. Güncel
  // prop'ları ref üzerinden oku.
  const propsRef = useRef({});
  propsRef.current = {
    navItems,
    navItemsIds,
    navItemSelected,
    containedViews,
    containedViewSelected,
    boxes,
    pluginToolbars,
    boxSelected,
    onNavItemSelected,
    onToolbarUpdated,
    onBoxDeleted,
    onBoxLayerChange,
    onInfographicApprove,
    onInfographicSceneApprove,
    onQuestionBatchApprove,
    onSceneVisualApprove,
    onBoxAdded,
  };

  // Tepsiden onaylanan/reddedilen/kaldırılan (ya da sunucu listesinde kapanmış
  // görünen) işlerin, modal dışında yaşayan kalıcı kayıtlarını kapat. Yoksa
  // modal tekrar açılınca zaten uygulanmış/reddedilmiş sonucu "Onayla" /
  // "Sahneye Ekle" ile geri getirir. Modal kapalıyken (unmount) de çalışsın
  // diye host'ta; açık modallar kendi state'lerini ayrıca sıfırlar.
  //   - ImageAnimatorModal: localStorage image-edit-job-<boxId>
  //   - Doğal konuşan avatar: talkingAvatarJobStore (sessionStorage)
  //   - Alan İşlemleri: localStorage area-ops-queue-<boxId> (devam eden pin)
  useEffect(
    () =>
      onJobsSettled((jobIds, outcomes) => {
        clearSettledImageEditJobs(jobIds);
        settleTalkingAvatarJobs(jobIds);
        settleAreaOpsStorage(jobIds, outcomes);
      }),
    []
  );

  // Bir job, artık var olmayan bir sahneye işaret ettiğinde (ör. video/avatar
  // job'ı kuyruğa girdikten sonra sahne silinip, iş kuyruğundan "yeniden ekle"
  // denildiğinde) hedef navId Redux state'inde bulunmaz. Böyle bir durumda
  // silinmiş sahneye gitmek beyaz ekrana yol açar ve eleman hiç eklenmez. Bu
  // helper, hedef geçerliyse onu, değilse ilk gerçek sahneyi döndürür — böylece
  // eleman ilk sahneye eklenir. EditorApp'teki
  // `navItemsIds.find((id) => id !== 0 && navItems[id])` kalıbıyla aynıdır.
  const resolveValidNavId = (desiredNavId) => {
    const { navItems: ni, navItemsIds: nids } = propsRef.current;
    if (desiredNavId && ni?.[desiredNavId]) return desiredNavId;
    return (Array.isArray(nids) ? nids.find((id) => id !== 0 && ni?.[id]) : null) || null;
  };

  // Callbacks passed directly to AsyncWidgetApp's HostBridge props.
  const handleAddToScene = (payload) => {
    // Doğal konuşan video kartları onAddToScene'i atlıyor; yine de buraya
    // gelirse sesli videoyu düz eklemek yerine aynı uygulama yolunu kullan.
    if (payload && payload.jobType && isTalkingAvatarJob({ type: payload.jobType })) {
      applyTalkingAvatarFromJob(
        { id: payload.jobId, type: payload.jobType, navId: payload.navId, result: payload.result },
        { approve: false, onNavigate: propsRef.current.onNavItemSelected }
      );
      return;
    }
    const { videoUrl, navId } = payload;
    const {
      navItemSelected: latestSel,
      onNavItemSelected: latestNav,
      navItems: latestNavItems,
    } = propsRef.current;
    const navIdSource = navId ? "payload.navId" : latestSel ? "current navItemSelected" : "NONE";
    console.log("[JobWidgetHost][TRACE] ADD_TO_SCENE ⇢ entry", {
      payloadNavId: navId,
      currentNavItemSelected: latestSel,
      chosenNavId: navId || latestSel,
      chosenSource: navIdSource,
      navItemExists: !!(navId || latestSel) && !!latestNavItems?.[navId || latestSel],
      videoUrl,
    });

    if (videoUrl) {
      // Silinmiş sahneye düşmeyi önle: geçersizse ilk sahneye ekle.
      const fallbackNavId = resolveValidNavId(navId || latestSel);
      if (!fallbackNavId) {
        console.warn("[JobWidgetHost][TRACE] ADD_TO_SCENE skipped: no navId and no selected nav", {
          navId,
        });
      } else {
        console.log("[JobWidgetHost][TRACE] ADD_TO_SCENE ⇢ calling addFileAsBox", {
          fallbackNavId,
        });
        addFileAsBoxRef.current?.("video", videoUrl, fallbackNavId);

        if (latestNav && fallbackNavId !== latestSel) {
          console.log("[JobWidgetHost][TRACE] ADD_TO_SCENE ⇢ scheduling navigation", {
            from: latestSel,
            to: fallbackNavId,
          });
          setTimeout(() => {
            console.log("[JobWidgetHost][TRACE] ADD_TO_SCENE ⇢ navigating now", {
              to: fallbackNavId,
            });
            latestNav(fallbackNavId);
          }, 100);
        } else {
          console.log("[JobWidgetHost][TRACE] ADD_TO_SCENE ⇢ no navigation needed", {
            target: fallbackNavId,
            current: latestSel,
            hasNavCallback: !!latestNav,
          });
        }
      }
    }
  };

  const handleJobApproved = (job) => {
    // === Task 40 === Recognise workflow-architect generation jobs so
    // the widget treats them as first-class citizens (same as TTS
    // avatar, scene-architect, image-animation). When the modal in
    // WorkflowOverlay (Task 39) is closed mid-job, this branch is the
    // only path that hears about completion — it re-emits the same
    // `workflow-architect-job` CustomEvent that GenerationModal uses
    // so any *re-opened* modal instance can pick up the terminal state.
    if (job?.type === "workflow-architect-generate") {
      try {
        window.dispatchEvent(
          new CustomEvent("workflow-architect-job", {
            detail: {
              type: job.status === "failed" ? "failed" : "completed",
              payload: {
                jobId: job.id || job.jobId,
                result: job.result,
                error: job.error,
              },
            },
          })
        );
      } catch (err) {
        console.warn("[JobWidgetHost] workflow-architect re-emit failed", err);
      }
      // Fall through so the generic LMS shell forwarder still fires.
    }
    // === /Task 40 ===

    // Doğal konuşan video: sahnenin anlatım sesi job'un dönüştürülmüş (STS)
    // sesiyle DEĞİŞTİRİLİR, video SESSİZ eklenir — her zaman job'un kendi
    // sahnesine. Aşağıdaki genel resultUrl / video dallarına düşmemeli: orada
    // ses değişmeden sesli video eklenirdi. Tray onayı kendisi gönderiyor
    // (approve:false); geçmişten "yeniden ekle" (approved) tekrar uygular.
    if (isTalkingAvatarJob(job)) {
      applyTalkingAvatarFromJob(job, {
        force: !!job.approved,
        approve: false,
        onNavigate: propsRef.current.onNavItemSelected,
      });
      return;
    }

    // Avatar canlandırma: klip ZATEN sahnedeki AvatarPlayer kutusuna ait, yeni
    // kutu eklenmez. Bu dal olmadan iş aşağıdaki genel `job.resultUrl + boxId`
    // daline düşer — orada tip kontrolü YOK ve applyAnimationResult kutuyu
    // EnrichedPlayer ile DEĞİŞTİRİR, yani avatar kutusu silinir. Job satırımız
    // hem resultUrl hem boxId taşıdığı için o dal bizi birebir yakalar.
    //
    // Normalde buraya hiç gelinmez (klip session_job_completed ile otomatik
    // uygulanıyor); bu, yazar karta yine de Approve derse diye güvenlik ağı.
    if ((job?.type || job?.jobType) === "avatar-animate") {
      applyAvatarClip({
        navId: job.navId || job.metadata?.navId,
        boxId: job.boxId || job.metadata?.boxId,
        videoUrl: job.resultUrl || job.metadata?.videoUrl || job.result?.videoUrl,
      });
      return;
    }

    // Motion Director (sohbetteki "Hareketli grafik tasarımı" kartı): sonuç bir
    // plan — kutular kartın kullandığı applyMotionPlan ile, işin KENDİ sahnesine
    // kurulur (aynı sahnede önceki "-mdir-" kutuları değiştirilir, yığılmaz).
    // Bu dal yokken tepsi onayı işi onaylıyor ama sahneye hiçbir şey eklemiyordu.
    if ((job?.type || job?.jobType) === "motion-director") {
      // The job's OWN scene only — never resolveValidNavId's first-scene fallback: the plan is
      // timed to that scene's images and replaces its -mdir- boxes.
      const wanted = job.navId || job.metadata?.navId || job.result?.navId;
      const navId = propsRef.current.navItems?.[wanted] ? wanted : null;
      if (!navId) {
        console.warn("[JobWidgetHost] motion-director approve SKIP: no scene", { job });
        return;
      }
      applyMotionResult(dispatch, store.getState, job.result, navId).then((out) => {
        if (out.error) {
          console.warn("[JobWidgetHost] motion-director apply failed", out.error, { job });
          return;
        }
        if (onNavItemSelected && navId !== propsRef.current.navItemSelected) {
          setTimeout(() => onNavItemSelected(navId), 150);
        }
      });
      return;
    }

    // Görsel Ekle → Hareketli Görsel / 3B Obje. Bu dal yokken tepsi işi
    // onaylıyor ama sahneye hiçbir şey eklemiyordu: tip adında "image" geçmediği
    // için görsel dalına, boxId/resultUrl taşımadığı için diğerlerine de
    // düşmüyordu. Modaldaki onay kartıyla aynı sonucu verir:
    //   • GIF / WebP → görsel kutusu, kaynak notu + 0'dan kendi süresince.
    //   • Standart motion / 3B → backend'in hazır kutusu (EditorApp).
    const sceneVisualType = job?.type || job?.jobType;
    if (sceneVisualType === "scene-visual-motion" || sceneVisualType === "scene-visual-3d") {
      const r = job.result && typeof job.result === "object" ? job.result : {};
      const targetNavId = resolveValidNavId(
        job.navId || job.metadata?.navId || r.navId || propsRef.current.navItemSelected
      );
      if (!targetNavId) {
        console.warn("[JobWidgetHost] scene-visual approve SKIP: no target scene", { job });
        return;
      }

      if (r.kind === "video") {
        // "Çevir" → MP4: kütüphaneden mp4 eklenmiş gibi, otomatik oynatma
        // açık. (Normalde pencere kendisi ekleyip kartı onaylıyor; bu, pencere
        // kapatılıp karttan eklenirse diye.)
        if (!r.videoUrl) {
          console.warn("[JobWidgetHost] scene-visual video approve SKIP: no video URL", { job });
          return;
        }
        addFileAsBoxRef.current?.("video", r.videoUrl, targetNavId, {
          initialState: { autoPlay: true },
        });
      } else if (r.kind === "image") {
        // "Çevir" → WebP: bir kere oynayan görsel, kütüphaneden eklenmiş gibi,
        // kendi süresince.
        if (!r.imageUrl) {
          console.warn("[JobWidgetHost] scene-visual webp approve SKIP: no image URL", { job });
          return;
        }
        addFileAsBoxRef.current?.("image", r.imageUrl, targetNavId, {
          timeline:
            Number(r.durationSec) > 0
              ? { durationHintFrames: Math.ceil(Number(r.durationSec) * FPS), visible: true }
              : undefined,
        });
      } else if (r.kind === "gif" || r.kind === "webp") {
        const imageUrl = r.imageUrl || extractGeneratedImageUrl(job);
        if (!imageUrl) {
          console.warn("[JobWidgetHost] scene-visual loop approve SKIP: no image URL", { job });
          return;
        }
        addFileAsBoxRef.current?.("image", imageUrl, targetNavId, {
          motionSource: r.htmlUrl
            ? {
                htmlUrl: r.htmlUrl,
                format: r.kind,
                orientation: r.orientation,
                model: r.model,
                imageUrl,
              }
            : undefined,
          timeline:
            Number(r.durationSec) > 0
              ? { visible: true, startFrame: 0, endFrame: Math.round(Number(r.durationSec) * FPS) }
              : undefined,
        });
      } else if (!propsRef.current.onSceneVisualApprove?.(r, targetNavId)) {
        console.warn("[JobWidgetHost] scene-visual approve SKIP: no box in result", { job });
        return;
      }

      const { navItemSelected: latestSel, onNavItemSelected: latestNav } = propsRef.current;
      if (latestNav && targetNavId !== latestSel) {
        setTimeout(() => latestNav(targetNavId), 100);
      }
      return;
    }

    // İnfografik "Sesten Görsel" job'ı: sonuç tek görsel değil, full sahne
    // (boxesById + timeline). Host tarafında yeniden kurmak yerine EditorApp'e
    // (onInfographicApprove) devrederiz — mode + hedef sahneyi o uygular.
    // Bu tip "image" içerdiği için aşağıdaki genel image dalından ÖNCE ele alınır.
    if (
      (job?.type || job?.jobType) === "infographic-images-generate" &&
      propsRef.current.onInfographicApprove
    ) {
      propsRef.current.onInfographicApprove(job);
      return;
    }

    // İnfografik "Sahne Üret" job'ı → yeni sahne olarak ekle (EditorApp).
    if (
      (job?.type || job?.jobType) === "infographic-scene-generate" &&
      propsRef.current.onInfographicSceneApprove
    ) {
      propsRef.current.onInfographicSceneApprove(job);
      return;
    }

    // Çoklu soru üret (question-batch-generate) → her soru bir full sahne
    // payload'ı (infographic-scene ile aynı shape); metadata.questions[]
    // içindeki her elemanı kendi kaynak sahnesinin (navId) ARDINA yeni
    // sahne olarak ekle. EditorApp'e devret (onQuestionBatchApprove).
    if (
      (job?.type || job?.jobType) === "question-batch-generate" &&
      propsRef.current.onQuestionBatchApprove
    ) {
      propsRef.current.onQuestionBatchApprove(job);
      return;
    }

    // İnfografik TEK görsel (infographic-image-generate): üretilen görsel,
    // sahnenin TAMAMINI kaplayacak (tam sığdır / 100%×100%) yeni bir box
    // olarak eklenir. Doğal boyutlu image-gen dalından ÖNCE ele alınır;
    // aksi halde jobType "image" içerdiği için orada küçük kutu olarak eklenir.
    if ((job?.type || job?.jobType) === "infographic-image-generate") {
      const generatedImageUrl = extractGeneratedImageUrl(job);
      const {
        navItemSelected: latestSel,
        onNavItemSelected: latestNav,
        navItems: latestNavItems,
        navItemsIds: latestNavIds,
      } = propsRef.current;

      const rawSceneIndex = job?.sceneIndex ?? job?.metadata?.sceneIndex;
      const sceneIdx =
        rawSceneIndex != null && rawSceneIndex !== "" ? parseInt(rawSceneIndex, 10) : NaN;
      const navIdFromScene =
        Number.isFinite(sceneIdx) && sceneIdx >= 1 && Array.isArray(latestNavIds)
          ? latestNavIds[sceneIdx - 1]
          : null;
      // Silinmiş sahneye düşmeyi önle: geçersizse ilk sahneye ekle.
      const targetNavId = resolveValidNavId(
        job?.navId ||
          (navIdFromScene && latestNavItems?.[navIdFromScene] ? navIdFromScene : null) ||
          latestSel
      );

      if (!generatedImageUrl) {
        console.warn("[JobWidgetHost] infographic-image-generate SKIP: no image URL", { job });
        return;
      }
      if (!targetNavId) {
        console.warn("[JobWidgetHost] infographic-image-generate SKIP: no target scene", {
          job,
        });
        return;
      }

      addFileAsBoxRef.current?.("image", generatedImageUrl, targetNavId, { fullScene: true });

      if (latestNav && targetNavId !== latestSel) {
        setTimeout(() => latestNav(targetNavId), 100);
      }
      return;
    }

    if (job?.type === "staged-video-init") {
      const storyboardId = job.metadata?.storyboardId;
      console.log("[JobWidgetHost][TRACE] staged-video-init ⇢", {
        storyboardId,
        // closure'dan gelen (stale olabilir)
        staleNavItemSelected: navItemSelected,
        // güncel ref'ten okunan
        currentNavItemSelected: propsRef.current.navItemSelected,
        jobNavId: job?.navId,
        willStoreInRef: navItemSelected,
      });
      if (storyboardId) {
        stagedNavIdRef.current = navItemSelected;
        // VideoModal tüm polling'i ve sahneye eklemeyi üstleniyor.
        // Sadece event gönder; JobWidgetHost'un kendi poller'ını başlatma.
        window.dispatchEvent(
          new CustomEvent("stagedVideoJobApproved", {
            detail: { storyboardId, job },
          })
        );
      } else {
        console.warn(
          "[JobWidgetHost] staged-video-init: storyboardId bulunamadı! metadata:",
          job?.metadata
        );
      }
      return;
    }

    if (job?.type === "staged-video-generation") {
      const {
        navItemSelected: latestSel,
        onNavItemSelected: latestNav,
        navItems: latestNavItems,
        boxes: latestBoxes,
      } = propsRef.current;

      // Backend bu tipte URL'i `job.resultUrl`'de DEĞİL, `job.result.videoUrl`
      // içinde döndürüyor (zengin sonuç objesi: videoUrl + metadata). Diğer
      // job tipleri düz `resultUrl` döndüğü için onlarda sorun yok. History'den
      // tıklamada aynı job'ın `resultUrl`'i de doluyor (muhtemelen widget cache),
      // bu yüzden orada da çalışıyor — ama canon source `result.videoUrl`.
      const videoUrl = job.resultUrl || job.result?.videoUrl;

      // Karar zincirini logla — hangisi DOLU, hangisi BOŞ?
      console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ entry", {
        "1_jobNavId (widget'tan)": job?.navId,
        "2_stagedNavIdRef (init'te kaydedilen)": stagedNavIdRef.current,
        "3_currentNavItemSelected (mevcut sayfa)": latestSel,
        resolvedVideoUrl: videoUrl,
        videoUrlSource: job.resultUrl
          ? "job.resultUrl"
          : job.result?.videoUrl
            ? "job.result.videoUrl"
            : "NONE",
        jobStoryboardId: job?.storyboardId || job?.metadata?.storyboardId,
        jobKeys: job ? Object.keys(job) : null,
      });

      if (!videoUrl) {
        console.warn("[JobWidgetHost][TRACE] staged-video-generation SKIP: no videoUrl", {
          job,
        });
        return;
      }
      // Silinmiş sahneye düşmeyi önle: geçersizse ilk sahneye ekle.
      const targetNavId = resolveValidNavId(job.navId || propsRef.current.navItemSelected);
      const targetSource = job.navId
        ? "job.navId (widget gönderdi)"
        : propsRef.current.navItemSelected
          ? "fallback: mevcut seçili sayfa"
          : "NONE";
      if (!targetNavId) {
        console.warn(
          "[JobWidgetHost][TRACE] staged-video-generation SKIP: no navId and no current nav",
          { job }
        );
        return;
      }

      // Hedef sayfa gerçekten state'te var mı?
      const targetNavItem = latestNavItems?.[targetNavId];
      console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ resolved target", {
        targetNavId,
        targetSource,
        navItemFound: !!targetNavItem,
        navItemType: targetNavItem?.type,
        navItemBoxes: targetNavItem?.boxes,
        currentSel: latestSel,
        willNavigate: !!latestNav && targetNavId !== latestSel,
      });

      // addFileAsBox öncesi/sonrası snapshot — kutu gerçekten eklendi mi?
      const beforeBoxIds = Object.keys(latestBoxes || {});
      console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ calling addFileAsBox", {
        targetNavId,
        beforeBoxCount: beforeBoxIds.length,
      });
      addFileAsBoxRef.current?.("video", videoUrl, targetNavId);
      // addFileAsBox sync olduğu için propsRef.current.boxes hemen güncellenmez
      // (next render'da yenilenir). Ama dispatch oldu mu kontrolü için microtask:
      queueMicrotask(() => {
        const afterBoxIds = Object.keys(propsRef.current.boxes || {});
        const newBoxes = afterBoxIds.filter((id) => !beforeBoxIds.includes(id));
        console.log(
          "[JobWidgetHost][TRACE] staged-video-generation ⇢ after addFileAsBox (microtask)",
          {
            afterBoxCount: afterBoxIds.length,
            newBoxIds: newBoxes,
          }
        );
      });

      if (latestNav && targetNavId !== latestSel) {
        console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ scheduling navigation", {
          from: latestSel,
          to: targetNavId,
        });
        setTimeout(() => {
          console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ navigating NOW", {
            to: targetNavId,
            currentSelAtFire: propsRef.current.navItemSelected,
          });
          latestNav(targetNavId);
        }, 100);
      } else {
        console.log("[JobWidgetHost][TRACE] staged-video-generation ⇢ NO navigation", {
          reason: !latestNav
            ? "no onNavItemSelected callback"
            : "target === current (zaten doğru sayfadayız)",
          target: targetNavId,
          current: latestSel,
        });
      }
      return;
    }

    // Image-generation jobs (text-to-image / style / direct): the result is a
    // brand-new image carried in result.images[].url (NOT job.resultUrl, which
    // image-EDITING uses) and there is no box to update — so commit it as a NEW
    // box on the scene it was generated for, navigating there first when we are
    // on a different scene. Mirrors staged-video-generation. Target scene =
    // job.navId, else the 1-based sceneIndex the request sent (mapped back to a
    // navId via the ordered id list), else the current scene. We do NOT gate on
    // boxId here: a fresh generation has no meaningful box to update, and the
    // jobType filter already excludes edit/animation jobs (which keep using the
    // resultUrl branch below).
    const jobTypeStr = (job?.type || job?.jobType || "").toLowerCase();
    // Bazı "image-editing" operasyonları aslında mevcut bir box'ı yerinde
    // düzenlemez, SIFIRDAN yeni bir görsel ÜRETİR (style-reference, similar,
    // from-reference, image-with-text, image-from-image). Bunlar text-to-image
    // gibi yeni bir box olarak eklenmeli. Aksi halde jobType "image-editing"
    // olduğu için /editing/ filtresine takılıp aşağıdaki resultUrl dalına
    // düşerler; orada da güncellenecek bir boxId olmadığından sessizce hiçbir
    // şey yapılmaz ve üretilen görsel editöre hiç ulaşmaz.
    const operationStr = (
      job?.operation ||
      job?.metadata?.operation ||
      job?.result?.operation ||
      ""
    ).toLowerCase();
    const GENERATIVE_IMAGE_OPS = new Set([
      "generate-with-style-reference",
      "generate-similar",
      "generate-from-reference",
      "generate-image-with-text",
      "generate-image-from-image",
    ]);
    const isEditLikeJob = /editing|animation|avatar|video|edit/.test(jobTypeStr);
    const isImageGenJob =
      (jobTypeStr.includes("image") && !isEditLikeJob) || GENERATIVE_IMAGE_OPS.has(operationStr);

    // "Edit Image" mevcut bir box'ı YERİNDE düzenler — modal'ın Accept akışı
    // gibi (onAcceptAnimation → boxId varsa url'i değiştir). Widget'ın job
    // listesinden kabul edilince bu op GENERATIVE_IMAGE_OPS'ta olduğundan "yeni
    // box ekle" dalına düşüp görseli olanın ÜSTÜNE ekliyordu. Kaynak box varsa
    // burada yakalayıp yerinde değiştir. (Kaynak box yoksa — ör. kütüphane
    // görseli — bu dal atlanır, aşağıdaki isImageGenJob dalı yeni box ekler.)
    //
    // NOT: op string birden çok isimle gelebiliyor (OP_TO_FEATURE_ID:
    // "edit-image", "edit" → generate-image-from-image). boxId da job'da veya
    // metadata/result altında olabilir. Hepsini normalize et.
    const EDIT_IN_PLACE_OPS = new Set(["generate-image-from-image", "edit-image", "edit"]);
    const editBoxId = job?.boxId || job?.metadata?.boxId || job?.result?.boxId;
    const editSrcBox = editBoxId && propsRef.current.boxes?.[editBoxId];
    // Teşhis: neden değiştirmiyor? Karar veren tüm değerleri dök.
    console.log("[JobWidgetHost][TRACE] edit-in-place decision", {
      jobTypeStr,
      operationStr,
      editBoxId,
      boxExists: !!editSrcBox,
      isImageGenJob,
      isEditLikeJob,
      hasResultUrl: !!job?.resultUrl,
      opInEditSet: EDIT_IN_PLACE_OPS.has(operationStr),
    });
    if (EDIT_IN_PLACE_OPS.has(operationStr) && editSrcBox) {
      const editedUrl = extractGeneratedImageUrl(job);
      if (!editedUrl) {
        console.warn("[JobWidgetHost] edit-image approve SKIP: no result URL", { job });
        return;
      }
      onToolbarUpdated(editBoxId, "main", "state", "url", editedUrl);
      // Yerinde düzenlemede box zaten mevcut sahnede. navId mevcut sahneyle
      // aynıysa onNavItemSelected'i ÇAĞIRMA — aynı sahneyi yeniden seçmek
      // sahneyi remount edip objelerin anlık kaybolup yer değiştirmesine yol
      // açıyor. Sadece gerçekten başka bir sahnedeysek nav et.
      if (job.navId && onNavItemSelected) {
        const validNavId = resolveValidNavId(job.navId);
        if (validNavId && validNavId !== propsRef.current.navItemSelected) {
          setTimeout(() => onNavItemSelected(validNavId), 150);
        }
      }
      return;
    }

    if (isImageGenJob) {
      const generatedImageUrl = extractGeneratedImageUrl(job);
      const {
        navItemSelected: latestSel,
        onNavItemSelected: latestNav,
        navItems: latestNavItems,
        navItemsIds: latestNavIds,
      } = propsRef.current;

      const rawSceneIndex = job?.sceneIndex ?? job?.metadata?.sceneIndex;
      const sceneIdx =
        rawSceneIndex != null && rawSceneIndex !== "" ? parseInt(rawSceneIndex, 10) : NaN;
      const navIdFromScene =
        Number.isFinite(sceneIdx) && sceneIdx >= 1 && Array.isArray(latestNavIds)
          ? latestNavIds[sceneIdx - 1]
          : null;
      // Silinmiş sahneye düşmeyi önle: geçersizse ilk sahneye ekle.
      const targetNavId = resolveValidNavId(
        job?.navId ||
          (navIdFromScene && latestNavItems?.[navIdFromScene] ? navIdFromScene : null) ||
          latestSel
      );

      console.log("[JobWidgetHost][TRACE] image-generation ⇢ approve", {
        jobType: jobTypeStr,
        jobBoxId: job?.boxId,
        generatedImageUrl,
        resultKeys:
          job?.result && typeof job.result === "object" ? Object.keys(job.result) : job?.result,
        rawSceneIndex,
        sceneIdx,
        navIdFromScene,
        jobNavId: job?.navId,
        targetNavId,
        currentSel: latestSel,
        willNavigate: !!latestNav && targetNavId !== latestSel,
      });

      if (!generatedImageUrl) {
        console.warn("[JobWidgetHost] image-generation approve SKIP: no image URL in job", {
          fullJob: job,
        });
        return;
      }
      if (!targetNavId) {
        console.warn("[JobWidgetHost] image-generation approve SKIP: no target scene", { job });
        return;
      }

      addFileAsBoxRef.current?.("image", generatedImageUrl, targetNavId);

      if (latestNav && targetNavId !== latestSel) {
        setTimeout(() => latestNav(targetNavId), 100);
      }
      return;
    }

    // extract-text: sonuç düz bir görsel swap'ı DEĞİL — temizlenmiş arka plan
    // (result.url) + tespit edilen her metin için bir bölge (result.textBoxes).
    // Modal "Accept" akışıyla aynı işi yapar: kaynağı temiz arka planla değiştir
    // ve her bölge için düzenlenebilir BasicText kutusu oluştur. Genel resultUrl
    // dalından ÖNCE ele alınır (o dal sadece box url'ini değiştirir, kutuları
    // oluşturmaz).
    if (operationStr === "extract-text" && job?.boxId) {
      const P = propsRef.current;
      const r = job.result && typeof job.result === "object" ? job.result : {};
      const md = job.metadata && typeof job.metadata === "object" ? job.metadata : {};
      const cleanUrl = r.url || job.resultUrl || md.imageUrl;
      const textBoxes = Array.isArray(r.textBoxes)
        ? r.textBoxes
        : Array.isArray(r.details?.textBoxes)
          ? r.details.textBoxes
          : Array.isArray(md.details?.textBoxes)
            ? md.details.textBoxes
            : [];

      const srcBox = P.boxes?.[job.boxId];
      const srcTb = P.pluginToolbars?.[job.boxId];

      // Ayıklanan yazı, kaynak görsel timeline'da ne zaman girip çıkıyorsa TAM
      // O ANDA girip çıksın diye kaynak görsel kutusunun start/end frame'ini
      // miras alır (yoksa yeni kutu frame 0'a düşüyordu). Sadece zamanlama
      // kopyalanır — visibility / interactions taşınmaz. boxCreator
      // initialParams.timeline'ı okuduğu için ids'e koymak yeterli.
      const srcTimeline =
        srcBox && srcBox.timeline && typeof srcBox.timeline === "object" ? srcBox.timeline : null;
      const inheritedTimeline = srcTimeline
        ? {
            startFrame: Number.isFinite(srcTimeline.startFrame) ? srcTimeline.startFrame : 0,
            ...(Number.isFinite(srcTimeline.endFrame) && { endFrame: srcTimeline.endFrame }),
          }
        : null;

      // 1) Kaynağı temizlenmiş arka planla değiştir.
      if (cleanUrl) {
        onToolbarUpdated(job.boxId, "main", "state", "url", cleanUrl);
      }

      if (srcBox && srcTb && textBoxes.length) {
        const pctNum = (v) => {
          const n = parseFloat(String(v == null ? "" : v).replace("%", ""));
          return isFinite(n) ? n : 0;
        };
        const escapeHtml = (s) =>
          (s || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
        // Bölge koordinatları GÖRSEL yüzdesi; canvas = kaynak orijini + bölge · kaynak boyutu.
        const sx = pctNum(srcBox.position && srcBox.position.x);
        const sy = pctNum(srcBox.position && srcBox.position.y);
        const sw = pctNum(srcTb.structure && srcTb.structure.width) || 100;
        const sh = pctNum(srcTb.structure && srcTb.structure.height) || 100;
        const owningNavId = resolveValidNavId(job.navId) || job.navId;

        textBoxes.forEach((tbx, i) => {
          const cx = sx + (pctNum(tbx.x) * sw) / 100;
          const cy = sy + (pctNum(tbx.y) * sh) / 100;
          const cw = Math.max(4, (pctNum(tbx.width) * sw) / 100);
          const html =
            `<p style="margin:0;"><span style="font-size:${tbx.fontSize};color:${tbx.color};` +
            `font-family:Arial,Helvetica,sans-serif;font-weight:700;line-height:1.25;">` +
            `${escapeHtml(tbx.content)}</span></p>`;
          const id = `${ID_PREFIX_BOX}${Date.now()}-txt-${i}-${Math.random()
            .toString(36)
            .slice(2, 5)}`;
          createBox(
            {
              id,
              parent: owningNavId,
              page: owningNavId,
              container: 0,
              position: {
                x: `${cx.toFixed(2)}%`,
                y: `${cy.toFixed(2)}%`,
                type: "absolute",
              },
              widthPercent: `${cw.toFixed(2)}%`,
              initialState: { __text: encodeURIComponent(html + "\n") },
              ...(inheritedTimeline && { timeline: inheritedTimeline }),
            },
            "BasicText",
            true,
            (...args) => P.onBoxAdded(...args),
            P.boxes,
            { padding: 0 }
          );
        });
      }

      if (job.navId && onNavItemSelected) {
        const validNavId = resolveValidNavId(job.navId);
        if (validNavId) setTimeout(() => onNavItemSelected(validNavId), 150);
      }
      return;
    }

    if (job?.resultUrl) {
      if (job.boxId) {
        // Owner directive: "Canlandır" (image-animation) sonucu döngülü
        // (looping) webp/gif olarak YERİNDE uygulanır — kutu kimliği
        // korunur, sadece url değişir. Bu yüzden görsel-formatlı çıktı
        // (webp, sonra gif) her zaman mp4'ten önce gelir; mp4 sadece hiç
        // görsel-formatlı çıktı yoksa (webp varsayılanından önceki eski
        // job'lar) seçilir — applyAnimationResult o durumda video url'i
        // görüp doğru şekilde EnrichedPlayer'a box-swap yapar.
        const r = job.result && typeof job.result === "object" ? job.result : {};
        const md = job.metadata && typeof job.metadata === "object" ? job.metadata : {};
        const url = pickAnimationApplyUrl({
          webpUrl: r.webpUrl || md.webpUrl,
          gifUrl: r.gifUrl || md.gifUrl,
          fallbackUrl: job.resultUrl,
          mp4Url: r.mp4Url || md.mp4Url,
        });
        const P = propsRef.current;

        const decision = applyAnimationResult({
          boxId: job.boxId,
          url,
          boxes: P.boxes,
          pluginToolbars: P.pluginToolbars,
          containedViewSelected: P.containedViewSelected,
          containedViews: P.containedViews,
          navItems: P.navItems,
          navItemSelected: P.navItemSelected,
        });

        if (
          decision.kind === "boxSwap" &&
          P.onBoxAdded &&
          IdealStudio.Plugins.get("EnrichedPlayer")
        ) {
          // Timeline satır sırası / z-order kaynağı navItemsById[parent].boxes
          // (veya contained view'sa containedViewsById[parent].boxes) — swap
          // öncesindeki sırayı yakalayıp aşağıda eski kutunun index'ini yeni
          // kutuya devrediyoruz (aksi halde ADD_BOX/DELETE_BOX videoyu her
          // zaman sıranın sonuna zıplatır).
          const srcParentIsCv = isContainedView(decision.parent);
          const parentBoxesBefore = srcParentIsCv
            ? P.containedViews?.[decision.parent]?.boxes
            : P.navItems?.[decision.parent]?.boxes;

          createBox(
            decision.newBox,
            "EnrichedPlayer",
            decision.inASlide,
            (...args) => P.onBoxAdded(...args),
            P.boxes
          );
          if (P.onBoxDeleted) {
            P.onBoxDeleted(decision.oldBoxId, decision.parent, decision.container, decision.page);
          }
          if (P.onBoxLayerChange && Array.isArray(parentBoxesBefore)) {
            const restoredOrder = parentBoxesBefore.map((id) =>
              id === job.boxId ? decision.newBoxId : id
            );
            P.onBoxLayerChange(decision.newBoxId, decision.parent, 0, "reorder", restoredOrder);
          }
        } else {
          onToolbarUpdated(job.boxId, "main", "state", "url", url);
        }
      }

      // image-animation ve diğer job tipleri için navId varsa o sayfaya git.
      // Sahne silinmişse geçersiz navId'ye gitmek beyaz ekrana yol açar;
      // ilk geçerli sahneye düş. Zaten o sahnedeysek yeniden seçme — aynı
      // sahneyi remount edip objelerin anlık kaybolmasına/yer değiştirmesine
      // yol açar (recall "yeniden ekle" flicker'ı).
      if (job.navId && onNavItemSelected) {
        const validNavId = resolveValidNavId(job.navId);
        if (validNavId && validNavId !== propsRef.current.navItemSelected) {
          setTimeout(() => {
            onNavItemSelected(validNavId);
          }, 150);
        }
      }

      setTimeout(() => {
        try {
          const event = new CustomEvent("imageAnimationCompleted", {
            detail: {
              newUrl: job.resultUrl,
              boxId: job.boxId,
              animationData: job,
            },
          });
          window.dispatchEvent(event);
        } catch (error) {
          console.warn("Could not dispatch animation completion event:", error);
        }

        try {
          if (window.location.pathname.includes("/editor")) {
            const boxElement = document.getElementById(`box-${job.boxId}`);
            if (boxElement) {
              const imgElements = boxElement.querySelectorAll("img");
              imgElements.forEach((img) => {
                if (img.src !== job.resultUrl) {
                  img.src = job.resultUrl;
                  img.onload = () => {
                    img.style.opacity = "0";
                    setTimeout(() => {
                      img.style.transition = "opacity 0.3s ease";
                      img.style.opacity = "1";
                    }, 50);
                  };
                }
              });
            }
          }
        } catch (error) {
          console.warn("Could not update image elements directly:", error);
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      }, 100);
    }
  };

  if (!trainingId) return null;

  // Portal to document.body: a deeply-nested ancestor stacking context (or
  // transformed ancestor) can trap position:fixed descendants below the
  // editor header even at z-index 999999. Escaping to <body> sidesteps that.
  return createPortal(
    <div className="aidea-async-widget">
      <AsyncWidgetApp
        trainingId={trainingId}
        getToken={getToken}
        openSignal={openSignal}
        hideFab={hideFab}
        onJobApproved={handleJobApproved}
        onAddToScene={handleAddToScene}
        onJobCompleted={handleJobCompleted}
      />
    </div>,
    document.body
  );
}
